import { useCallback, useMemo } from 'react';

import {
  CampaignListFilterDefinitionKey,
  getCampaignListFilter,
  readCampaignListFilters,
  removeCampaignListFilter as removeStoredCampaignListFilter,
  updateCampaignListFilter,
} from '@features/campaigns/list/utils/campaignListFiltersStorage.ts';
import { FiltersType } from '@features/campaigns/shared/types/filters.ts';
import { useUserDetails } from '@shared/providers/UserDetailsProvider/useUserDetails.tsx';

type FilterType = FiltersType | 'search';

const toCampaignListFilterKey = (filterName: FilterType): CampaignListFilterDefinitionKey => filterName;

export const useCampaignsListFiltersStorage = () => {
  const { id } = useUserDetails();

  const storedFilters = useMemo(() => {
    if (!id) {
      return null;
    }

    return readCampaignListFilters(id);
  }, [id]);

  const getFilter = useCallback(
    (filterName: FilterType): string | undefined => {
      if (!id) {
        return undefined;
      }

      return getCampaignListFilter(id, toCampaignListFilterKey(filterName));
    },
    [id],
  );

  const setFilter = useCallback(
    (filterName: FilterType, filterValue: string) => {
      if (!id) {
        return;
      }

      updateCampaignListFilter(id, toCampaignListFilterKey(filterName), filterValue);
    },
    [id],
  );

  const removeFilter = useCallback(
    (filterName: FilterType) => {
      if (!id) {
        return;
      }

      removeStoredCampaignListFilter(id, toCampaignListFilterKey(filterName));
    },
    [id],
  );

  return {
    userId: id,
    storedFilters,
    getFilter,
    setFilter,
    removeFilter,
  };
};
